import { ChangeDetectionStrategy, Component, OnDestroy } from '@angular/core';
import { BehaviorSubject, EMPTY, Observable, Subject, Subscription } from 'rxjs';
import { catchError, exhaustMap, finalize, map, tap } from 'rxjs/operators';
import { OdooToastService } from 'src/app/services/odoo/odoo-toast.service';
import { OdooArchivoClaseDia, OdooArchivoDia, OdooCarga, OdooResumen } from 'src/app/services/odoo/odoo.models';
import { OdooService, conCarga, mensajeDeFallo } from 'src/app/services/odoo/odoo.service';

const ZONA = 'America/Caracas';
const fHora = new Intl.DateTimeFormat('es-VE', { hour: 'numeric', minute: '2-digit', hour12: true, timeZone: ZONA });
const fDiaCorto = new Intl.DateTimeFormat('es-VE', { day: 'numeric', month: 'short', timeZone: ZONA });
const fDiaClave = new Intl.DateTimeFormat('en-CA', { timeZone: ZONA });
const fSemana = new Intl.DateTimeFormat('es-VE', { weekday: 'long', timeZone: ZONA });
const fSemanaUtc = new Intl.DateTimeFormat('es-VE', { weekday: 'long', timeZone: 'UTC' });
const fMesUtc = new Intl.DateTimeFormat('es-VE', { month: 'short', timeZone: 'UTC' });

const mayus = (t: string): string => t.charAt(0).toUpperCase() + t.slice(1);
const hora = (d: Date): string => fHora.format(d).replace(/\s/g, ' ');
const mesCorto = (t: string): string => t.replace('.', '');
const miles = (n: number): string => String(n).replace(/\B(?=(\d{3})+(?!\d))/g, '.');

export interface DiaVm { fecha: string; titulo: string; detalle: string; ok: boolean; }
export interface ResumenVm {
  r: OdooResumen;
  conectado: boolean;
  conexionTitulo: string;
  conexionDetalle: string;
  anotadosClientes: string;
  anotadosComercios: string;
  reintento: string;
  problemaPie: string;
  proxDia: string;
  proxHora: string;
  dias: DiaVm[];
  chips: { icono: string; texto: string }[];
}

/** Pura: "probado hoy 8:39 a. m." / "probado el 16 oct · 8:39 a. m.". */
export function textoProbado(iso: string | null, ahora = new Date()): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  return fDiaClave.format(d) === fDiaClave.format(ahora)
    ? `probado hoy ${hora(d)}`
    : `probado el ${mesCorto(fDiaCorto.format(d))} · ${hora(d)}`;
}

/** Pura: la próxima hora en punto (los reintentos corren cada hora). */
export function proximaHoraEnPunto(ahora = new Date()): string {
  return hora(new Date(Math.floor(ahora.getTime() / 3600000 + 1) * 3600000));
}

/** "Domingo 18-oct" a partir de YYYY-MM-DD. */
export function tituloDia(fecha: string): string {
  const d = new Date(`${fecha.slice(0, 10)}T00:00:00Z`);
  if (isNaN(d.getTime())) return fecha;
  return `${mayus(fSemanaUtc.format(d))} ${d.getUTCDate()}-${mesCorto(fMesUtc.format(d))}`;
}

function detalleDia(d: OdooArchivoDia): DiaVm {
  const partes: string[] = [];
  let error: string | null = null;
  const clases: [string, OdooArchivoClaseDia | null][] = [['Giros', d.creditos], ['Pagos', d.pagos]];
  for (const [nombre, c] of clases) {
    if (!c) continue;
    if (c.estado === 'error') error = error || c.error || 'No se pudo subir';
    else partes.push(`${nombre} ${c.filas} filas`);
  }
  const detalle = error ? (partes.length ? `${error} · ${partes.join(' · ')}` : error) : partes.join(' · ');
  return { fecha: d.fecha, titulo: tituloDia(d.fecha), detalle, ok: !error };
}

/**
 * RESUMEN — ¿está conectado?, ¿cuántos faltan?, ¿subió el archivo de ayer? Una sola petición (/resumen).
 * Sin polling: al volver a la pestaña se pinta lo último visto y se refresca en silencio.
 */
@Component({
  selector: 'app-odoo-resumen',
  templateUrl: './odoo-resumen.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OdooResumenComponent implements OnDestroy {
  private readonly recargar$ = new BehaviorSubject<void>(undefined);
  private readonly probar$ = new Subject<void>();
  private readonly subs = new Subscription();
  readonly probando$ = new BehaviorSubject<boolean>(false);

  readonly vm$: Observable<OdooCarga<ResumenVm>> = conCarga(this.recargar$, () => this.odoo.resumen()).pipe(
    map((c) => (!c.datos && !c.error && this.odoo.resumenGuardado ? { ...c, datos: this.odoo.resumenGuardado } : c)),
    map((c): OdooCarga<ResumenVm> => ({ cargando: c.cargando, error: c.error, datos: c.datos ? this.armar(c.datos) : null })),
  );

  readonly esperaSkeleton = [0, 1, 2, 3];

  constructor(private odoo: OdooService, private toast: OdooToastService) {
    this.subs.add(
      this.probar$.pipe(
        exhaustMap(() => {
          this.probando$.next(true);
          return this.odoo.probarConexion().pipe(
            tap((p) => this.toast.mostrar(p.mensaje, p.estado === 'ok')),
            catchError((e: unknown) => { this.toast.mostrar(mensajeDeFallo(e), false); return EMPTY; }),
            finalize(() => { this.probando$.next(false); this.recargar$.next(); }),
          );
        }),
      ).subscribe(),
    );
  }

  ngOnDestroy(): void { this.subs.unsubscribe(); }

  recargar = (): void => this.recargar$.next();
  probar = (): void => this.probar$.next();
  porFecha = (_: number, d: DiaVm): string => d.fecha;
  porTexto = (_: number, c: { texto: string }): string => c.texto;
  porIndice = (i: number): number => i;

  private armar(r: OdooResumen): ResumenVm {
    const cx = r.conexion;
    const ahora = new Date();
    const conectado = cx.estado === 'ok';
    const prox = r.proxima_subida ? new Date(r.proxima_subida) : null;
    const cfg = r.config_resumen;
    const dias = (r.archivos_por_dia || []).map(detalleDia);
    return {
      r,
      conectado,
      conexionTitulo: conectado ? 'Conectado' : cx.estado === 'sin_configurar' ? 'Sin configurar' : 'Sin conexión',
      conexionDetalle: conectado ? textoProbado(cx.probado_en, ahora) : cx.mensaje,
      anotadosClientes: `${miles(r.maestros.por_tipo.cliente.anotados)} clientes`,
      anotadosComercios: `${miles(r.maestros.por_tipo.comercio.anotados)} comercios`,
      reintento: r.maestros.pendientes > 0 ? `se reintenta ${proximaHoraEnPunto(ahora)}` : 'nada en espera',
      problemaPie: r.maestros.errores > 0 ? (r.problema_frecuente?.motivo || 'Hay que revisarlos') : 'Todo en orden',
      proxDia: cfg.archivos_activo && prox && !isNaN(prox.getTime())
        ? `${fSemana.format(prox)} ${prox.toLocaleDateString('es-VE', { day: 'numeric', timeZone: ZONA })}-${mesCorto(fDiaCorto.format(prox).replace(/^\d+\s*/, ''))}`
        : 'Apagado',
      proxHora: cfg.archivos_activo && prox && !isNaN(prox.getTime()) ? hora(prox) : 'subida automática apagada',
      dias,
      chips: [
        { icono: cfg.enabled ? 'check' : 'x', texto: cfg.enabled ? 'Avisos activos' : 'Avisos apagados' },
        { icono: cfg.crear_activo ? 'check' : 'x', texto: cfg.crear_activo ? 'Clientes nuevos se anotan' : 'Clientes nuevos no se anotan' },
        { icono: cfg.modificar_activo ? 'check' : 'x', texto: cfg.modificar_activo ? 'Cambios se avisan' : 'Cambios no se avisan' },
        { icono: 'server', texto: cfg.buzon_tipo === 'drive' ? 'Google Drive' : 'Buzón de prueba' },
      ],
    };
  }
}
